import { Box, Button } from '@mui/material';
import React, { useContext } from 'react';
import { useLanguage, useSupportedCountries } from 'src/common/hooks';
import Autocomplete from 'src/components/Autocomplete';
import TextField from 'src/components/TextField';
import { mapError } from '../../../utils/validation';
import { StyledPanel, StyledTitle } from '../../index.styles';
import { UnitFormValidationContext } from './unit-utils';
import { toast } from 'react-toastify';
import { useGlnCodes } from 'src/components/hooks';

type Country = {
  name: string;
  alpha3: string;
};

export type LocationState = {
  city: string;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  country: string | null;
  setCountry: (country: string | null) => void;
  glnCodeValue: string | null;
  setGlnCodeValue: (glnCodeValue: string | null) => void;
};

export const useLocation = (): LocationState => {
  const [country, setCountry] = React.useState<string | null>(null);
  const [city, setCity] = React.useState<string>('');
  const [glnCodeValue, setGlnCodeValue] = React.useState<string | null>(null);
  return {
    country,
    setCountry,
    city,
    setCity,
    glnCodeValue,
    setGlnCodeValue,
  };
};

type Props = LocationState & {};

export default function Location(props: Props) {
  const { t } = useLanguage();
  const { country, setCountry, city, setCity, glnCodeValue, setGlnCodeValue } =
    props;

  const errors = useContext(UnitFormValidationContext);

  const { data: countries } = useSupportedCountries();

  const { fetchGlnCode } = useGlnCodes();

  const fetchGlnCodeByValue = async () => {
    if (!glnCodeValue) {
      return;
    }
    const response = await fetchGlnCode.mutateAsync(glnCodeValue);
    const { data } = response;
    if (data.length === 0) {
      toast.error(
        t('superadmin:components.panels.unit.location.glnCode.error') as string
      );
      return;
    }
    toast.success(
      t('superadmin:components.panels.unit.location.glnCode.success') as string
    );
    if (data[0].city) {
      setCity(data[0].city);
    }
  };

  return (
    <div>
      <StyledTitle sx={{ fontSize: '28px', mt: '40px' }}>
        {t('superadmin:components.panels.unit.location.title')}
      </StyledTitle>

      <StyledPanel sx={{ mt: '32px', p: '32px' }}>
        <Autocomplete
          label={t('superadmin:components.panels.unit.location.country.label')}
          placeholder={t(
            'superadmin:components.panels.unit.location.country.placeholder'
          )}
          options={countries?.map((country: Country) => ({
            value: country.alpha3,
            label: `${country.name} (${country.alpha3})`,
          }))}
          value={country}
          onChange={setCountry}
          sx={{ mb: '24px' }}
          errorText={mapError(errors, 'country', country)}
        />
        <TextField
          fullWidth
          value={city}
          onChange={(e) => setCity(e.target.value)}
          label={t('superadmin:components.panels.unit.location.city.label')}
          placeholder={t(
            'superadmin:components.panels.unit.location.city.placeholder'
          )}
          errorText={mapError(errors, 'city', city)}
          sx={{ mb: '24px' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            fullWidth
            value={glnCodeValue}
            onChange={(e) => setGlnCodeValue(e.target.value)}
            label={t(
              'superadmin:components.panels.unit.location.glnCode.label'
            )}
            placeholder={t(
              'superadmin:components.panels.unit.location.glnCode.placeholder'
            )}
            errorText={mapError(errors, 'gln_code_value', glnCodeValue)}
          />
          <Button onClick={fetchGlnCodeByValue}>
            {t('superadmin:components.panels.unit.location.glnCode.button')}
          </Button>
        </Box>
      </StyledPanel>
    </div>
  );
}
